import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from './auth/AuthProvider';
import { Shell } from './components/Shell';
import { Empty, Loading } from './components/ui';
import { useI18n } from './i18n';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Invoices } from './pages/Invoices';
import { InvoiceDetail } from './pages/InvoiceDetail';
import { InvoiceNew } from './pages/InvoiceNew';
import { InvoiceImport } from './pages/InvoiceImport';
import { Dossier } from './pages/Dossier';
import { DossierGaps } from './pages/DossierGaps';
import { PurchaseInvoiceDetail } from './pages/PurchaseInvoiceDetail';
import { InvoiceMailbox } from './pages/InvoiceMailbox';
import { Recurring } from './pages/Recurring';
import { RecurringEdit } from './pages/RecurringEdit';
import { Customers } from './pages/Customers';
import { CustomerEdit } from './pages/CustomerEdit';
import { Tickets } from './pages/Tickets';
import { TicketDetail } from './pages/TicketDetail';
import { MailLog } from './pages/MailLog';
import { Dunning } from './pages/Dunning';
import { Templates } from './pages/Templates';
import { Portal } from './pages/Portal';
import { Settings } from './pages/Settings';
import { CompanyNew } from './pages/CompanyNew';
import { PublicMyData } from './pages/PublicMyData';
import { CrmPipeline } from './pages/crm/CrmPipeline';
import { CrmAccounts } from './pages/crm/CrmAccounts';
import { CrmAccountDetail } from './pages/crm/CrmAccountDetail';
import { CrmPeople } from './pages/crm/CrmPeople';
import { CrmTasks } from './pages/crm/CrmTasks';
import { CrmFunnel } from './pages/crm/CrmFunnel';
import { CrmImport } from './pages/crm/CrmImport';
import { CrmOutreach } from './pages/crm/CrmOutreach';

export function App() {
  const { loading, user, company, role } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  // The public "My data" page works without signing in (AVI-13 §8a).
  if (location.pathname.startsWith('/my-data/')) {
    return <Routes><Route path="/my-data/:slug/:token?" element={<PublicMyData />} /></Routes>;
  }
  if (loading) return <Loading />;
  if (!user) return <Login />;
  // Global admins see every company, so an empty list means the platform has none yet.
  if (!company) return user.isGlobalAdmin ? <CompanyNew first /> : <Login noMembership />;
  if (role === 'customer' && location.pathname !== '/portal') return <Navigate to="/portal" replace />;
  // A crm member gets the CRM pages only — nothing of billing.
  if (role === 'crm') {
    return (
      <Routes>
        <Route element={<Shell />}>
          {company.crmEnabled && crmRoutes(false)}
          <Route path="*" element={company.crmEnabled ? <Navigate to="/crm/pipeline" replace /> : <Empty>{t('crmOffForRole')}</Empty>} />
        </Route>
      </Routes>
    );
  }
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Dashboard />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/new" element={<InvoiceNew />} />
        <Route path="invoices/import" element={<InvoiceImport />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="dossier" element={<Dossier />} />
        <Route path="dossier/gaps" element={<DossierGaps />} />
        <Route path="dossier/:id" element={<PurchaseInvoiceDetail />} />
        <Route path="mailbox" element={<InvoiceMailbox />} />
        <Route path="recurring" element={<Recurring />} />
        <Route path="recurring/:id" element={<RecurringEdit />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:id" element={<CustomerEdit />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="tickets/:id" element={<TicketDetail />} />
        <Route path="mail" element={<MailLog />} />
        <Route path="dunning" element={<Dunning />} />
        <Route path="templates" element={<Templates />} />
        <Route path="portal" element={<Portal />} />
        <Route path="settings" element={<Settings />} />
        {company.crmEnabled && crmRoutes(role === 'admin')}
        {user.isGlobalAdmin && <Route path="companies/new" element={<CompanyNew />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function crmRoutes(admin: boolean) {
  return (
    <>
      <Route path="crm" element={<Navigate to="/crm/pipeline" replace />} />
      <Route path="crm/pipeline" element={<CrmPipeline />} />
      <Route path="crm/accounts" element={<CrmAccounts />} />
      <Route path="crm/accounts/:id" element={<CrmAccountDetail />} />
      <Route path="crm/people" element={<CrmPeople />} />
      <Route path="crm/tasks" element={<CrmTasks />} />
      <Route path="crm/funnel" element={<CrmFunnel />} />
      <Route path="crm/outreach" element={<CrmOutreach />} />
      {admin && <Route path="crm/import" element={<CrmImport />} />}
    </>
  );
}
